/**
 * Who won.
 *
 * Three decisions, all of which come from what a leaderboard is actually
 * read for on a Saturday afternoon.
 *
 * It says what settled a tie. Not as a footnote — on the row, in the
 * place. "Lost on the back nine" is the only sentence that ends the
 * conversation at the counter, and a board that shows two names on the
 * same line without it starts the conversation instead.
 *
 * It says it is provisional while cards are still out, and it says how
 * many. A board that looks final and is not is worse than no board.
 *
 * And the people who returned no card are named, not counted. "Four to
 * find" sends somebody looking; the names tell them who to look for.
 */

import { useApi } from '../lib/useApi.ts';
import { Card, Empty, ErrorBanner, Loading, Status } from './ui.tsx';
import { date as fmtDate } from '../lib/format.ts';

export interface LeaderRow {
  score_id: string;
  person_id: string;
  player_name: string;
  entry_id: string | null;
  team_name: string | null;
  holes_played: number;
  gross: number;
  playing_handicap: number;
  net: number;
  points: number;
  score: number;
  place: number;
  is_complete: boolean;
  tie_broken_on: string | null;
}

export interface CompetitionRow {
  competition_id: string;
  event_id: string;
  event_name: string;
  play_date: string;
  name: string;
  format: string;
  handicap_allowance: string | number;
  division: string | null;
  places_paid: number | null;
  status: string;
  finalised_at: string | null;
  finalised_by_name: string | null;
  notes: string | null;
  cards_in: number;
  no_returns: number;
}

export interface CompetitionPayload {
  competition: CompetitionRow;
  leaderboard: LeaderRow[];
  no_returns: { person_id: string; player_name: string; team_name: string | null }[];
  is_provisional: boolean;
}

export const FORMAT_LABEL: Record<string, string> = {
  gross: 'Gross',
  net: 'Net',
  stableford: 'Stableford',
  best_ball: 'Better ball',
};

/** 1st, 2nd, 3rd — and "T3" where a place is shared. */
function placeLabel(place: number, shared: boolean): string {
  const s = place % 10 === 1 && place !== 11 ? 'st'
    : place % 10 === 2 && place !== 12 ? 'nd'
      : place % 10 === 3 && place !== 13 ? 'rd' : 'th';
  return `${shared ? 'T' : ''}${place}${s}`;
}

export function Leaderboard({
  competitionId, reloadKey = 0,
}: { competitionId: string; reloadKey?: number }) {
  const q = useApi<CompetitionPayload>(
    `/v1/golf/competitions/${competitionId}`, [competitionId, reloadKey]);

  const d = q.data;
  if (q.loading) return <Card><Loading /></Card>;
  if (!d) return <Card><ErrorBanner error={q.error} /></Card>;

  const c = d.competition;
  const stableford = c.format === 'stableford';
  // The figure the board is actually ranked on. `score` is that number
  // whatever the format, and the bold column has to be it: a gross
  // competition that bolds net shows rows in an order the bold column
  // does not explain, which reads as a broken leaderboard.
  const gross = c.format === 'gross';
  const scoreLabel = stableford ? 'Points' : gross ? 'Gross' : 'Net';
  const allowance = Number(c.handicap_allowance);

  // Which places are shared, so a row can say "T3" rather than pretending
  // to an outright placing it does not have.
  const counts = new Map<number, number>();
  for (const r of d.leaderboard) counts.set(r.place, (counts.get(r.place) ?? 0) + 1);

  return (
    <Card
      title={`${c.event_name} — ${c.name}`}
      actions={
        <span className="muted small">
          {FORMAT_LABEL[c.format] ?? c.format}
          {allowance !== 1 && ` · ${Math.round(allowance * 100)}% handicap`}
          {c.division && ` · ${c.division}`}
        </span>
      }
    >
      <ErrorBanner error={q.error} />

      <div className="leader__state">
        {c.status === 'final' ? (
          <Status tone="good">
            Declared {c.finalised_at ? fmtDate(c.finalised_at) : ''}
            {c.finalised_by_name ? ` by ${c.finalised_by_name}` : ''}
          </Status>
        ) : d.is_provisional ? (
          // A board that looks final and is not is worse than no board.
          <Status tone="warning">
            Provisional
            {d.no_returns.length > 0
              && ` — ${d.no_returns.length} card${
                d.no_returns.length === 1 ? '' : 's'} still out`}
          </Status>
        ) : (
          <Status tone="neutral">All cards in, not yet declared</Status>
        )}
      </div>

      {d.leaderboard.length === 0 ? (
        <Empty>
          No cards yet. A leaderboard is worked out from the cards the shop enters —
          nothing on it is typed, so it moves as they come in.
        </Empty>
      ) : (
        <table className="data leader">
          <thead>
            <tr>
              <th className="num">Pos</th>
              <th>Player</th>
              <th className="num">Hcp</th>
              {!gross && <th className="num">Gross</th>}
              <th className="num">{scoreLabel}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {d.leaderboard.map((r) => {
              const shared = (counts.get(r.place) ?? 1) > 1;
              const paid = c.places_paid !== null && r.place <= c.places_paid;

              return (
                <tr key={r.score_id} className={paid ? 'leader__paid' : undefined}>
                  <td className="num mono">{placeLabel(r.place, shared)}</td>
                  <td>
                    {r.player_name}
                    {r.team_name && <div className="hint">{r.team_name}</div>}
                  </td>
                  <td className="num">{r.playing_handicap}</td>
                  {!gross && <td className="num">{r.gross}</td>}
                  <td className="num"><strong>{r.score}</strong></td>
                  <td className="small">
                    {/* The sentence that ends the argument, on the row
                        rather than in a footnote. */}
                    {r.tie_broken_on && r.tie_broken_on !== 'nothing — a genuine tie' && (
                      <span className="muted">lost on the {r.tie_broken_on}</span>
                    )}
                    {r.tie_broken_on === 'nothing — a genuine tie' && (
                      <span className="muted">tied outright</span>
                    )}
                    {!r.is_complete && (
                      <span className="muted">
                        {r.holes_played} holes
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {d.no_returns.length > 0 && (
        <>
          <h3 className="small" style={{ marginTop: 16 }}>No card returned</h3>
          <p className="hint">
            {/* Named, not counted. These are people to go and find, and a
                number cannot be looked for. */}
            {d.no_returns.map((n) => n.player_name).join(', ')}.
          </p>
        </>
      )}
    </Card>
  );
}


/* ------------------------------------------------------------------ */
/* Where a member finished                                             */
/* ------------------------------------------------------------------ */

interface MyResult {
  competition_id: string;
  event_name: string;
  name: string;
  format: string;
  status: string;
  play_date: string;
  place: number;
  field_size: number;
  /** What this format is ranked on — gross, net or points. */
  score: number;
  gross: number;
  net: number;
  points: number;
  playing_handicap: number;
  tie_broken_on: string | null;
  is_complete: boolean;
}

/**
 * A member's own placings.
 *
 * The question they actually ask, which a leaderboard does not answer
 * without them reading down it looking for their own name. Undeclared
 * competitions are included and marked, because "provisional, you are
 * second" is worth knowing on the Saturday afternoon — which is exactly
 * when somebody looks.
 */
export function MyResults() {
  const q = useApi<{ results: MyResult[] }>('/v1/golf/results?days=120');
  const rows = q.data?.results ?? [];

  if (q.loading || rows.length === 0) return null;

  return (
    <Card title="How You Finished">
      <table className="data">
        <thead>
          <tr>
            <th>Day</th><th>Competition</th>
            <th className="num">Score</th><th className="num">Place</th><th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.competition_id}>
              <td>{fmtDate(r.play_date)}</td>
              <td>
                {r.event_name}
                <div className="hint">
                  {r.name} · {FORMAT_LABEL[r.format] ?? r.format}
                </div>
              </td>
              <td className="num">
                {/* The number the competition was decided on — which is
                    gross in a scratch event, and was showing as net. */}
                <strong>{r.score}</strong>
                <div className="hint">
                  {r.format === 'gross'
                    ? 'played gross — no handicap'
                    : `${r.gross} off ${r.playing_handicap}`}
                </div>
              </td>
              <td className="num mono">
                {placeLabel(r.place, false)}
                <div className="hint">of {r.field_size}</div>
              </td>
              <td className="small">
                {r.status !== 'final' && <Status tone="warning">Provisional</Status>}
                {/* The sentence somebody would otherwise ring the shop
                    about. */}
                {r.tie_broken_on && r.tie_broken_on !== 'nothing — a genuine tie' && (
                  <div className="hint">lost on the {r.tie_broken_on}</div>
                )}
                {r.tie_broken_on === 'nothing — a genuine tie' && (
                  <div className="hint">tied outright</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
